import type { AnswerQuestionMultiple, FeedbackAnswerQuestionMultiple, FeedbackAnswerQuestionMultipleItem } from '@repo/types/questions/QuestionMultiple';

import { getCorrectTypeColor } from '@repo/lib/quiz/getCorrectTypeColor';
import { useEffect } from 'react';
import { useFieldArray, useForm, useWatch } from 'react-hook-form';

import {
  QuestionMarkedCorrectToggleField,
  QuestionMarkedExplanationField,
  QuestionMarkedFormActions,
  QuestionMarkedPointsField,
  QuestionMarkedResponseCard,
  QuestionMarkedResponseContent,
  QuestionMarkedResponseHeader,
} from '@/components/features/quiz/question/question-marked/QuestionMarked';
import { Form, FormField } from '@/components/ui/form';
import { Markdown } from '@/components/ui/markdown';

import type { QuestionMarkedMultipleFormProps } from '.';

export function QuestionMarkedMultipleForm(questionMarkedMultipleFormProps: QuestionMarkedMultipleFormProps) {
  const { marked, isOpen, onClose, onSubmit: _onSubmit, content, maxPoints } = questionMarkedMultipleFormProps;

  const form = useForm<FeedbackAnswerQuestionMultiple>({
    defaultValues: {
      answers: [],
    },
  });

  const { fields } = useFieldArray({
    control: form.control,
    name: 'answers',
    keyName: 'fieldKey',
  });

  const watchedAnswers = useWatch({
    control: form.control,
    name: 'answers',
  });

  const getMaxAssignablePoints = (answers: FeedbackAnswerQuestionMultipleItem[] | null, answerIndex: number) => {
    const list: FeedbackAnswerQuestionMultipleItem[] = answers ?? [];
    const otherPoints: number = list.reduce((sum, a, i) => (i === answerIndex ? sum : sum + (a.points || 0)), 0);
    return Number(Math.max(0, maxPoints - otherPoints).toFixed(2));
  };

  const onSubmit = (data: FeedbackAnswerQuestionMultiple) => {
    onClose();
    _onSubmit?.(data);
  };

  useEffect(() => {
    if (isOpen && marked) {
      form.reset(marked);
    }
  }, [isOpen, marked, form]);

  return (
    <Form {...form}>
      <form className={'flex w-full flex-col gap-3 p-3'} onSubmit={form.handleSubmit(onSubmit)} noValidate>
        <div className="space-y-4">
          {fields.map((field, index) => {
            const answerId: string = field.id;
            const answer: AnswerQuestionMultiple | undefined = content?.answers.find((a) => a.id === answerId);
            const maxAssignable: number = getMaxAssignablePoints(watchedAnswers, index);
            return (
              <QuestionMarkedResponseCard key={field.fieldKey}>
                <QuestionMarkedResponseHeader>
                  <QuestionMarkedCorrectToggleField control={form.control} name={`answers.${index}.correct`} />
                  <QuestionMarkedPointsField control={form.control} name={`answers.${index}.points`} maxPoints={maxAssignable} />
                </QuestionMarkedResponseHeader>

                <QuestionMarkedResponseContent>
                  <FormField
                    name={`answers.${index}.correct`}
                    control={form.control}
                    render={({ field: { value } }) => (
                      <Markdown content={answer?.content ?? ''} textClassName={getCorrectTypeColor(value)} imageClassName="max-h-68 rounded-sm" videoClassName="max-h-68 rounded-sm" />
                    )}
                  />
                </QuestionMarkedResponseContent>

                <QuestionMarkedExplanationField control={form.control} name={`answers.${index}.explanation`} />
              </QuestionMarkedResponseCard>
            );
          })}
        </div>
        <QuestionMarkedFormActions onClose={onClose} />
      </form>
    </Form>
  );
}
